import _ from 'lodash';
import { sqTrendStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { fetchPlot } from '@/scatterPlot/scatterPlot.actions';
import {
  fetchAllStatistics,
  fetchChartCapsules,
  fetchDataBasedOnView,
  fetchPropsForAllItems,
  fetchTableAndChartCapsules,
  resetCapsulePanelOffset,
  setCapsulePanelOffset,
} from '@/trendData/trend.actions';
import { recomputeChartCapsules } from '@/trendData/trendCapsule.actions';
import {
  CUSTOMIZATION_MODES,
  PROPERTIES_COLUMN_PREFIX,
  PROPERTIES_UOM_COLUMN_PREFIX,
  SERIES_PANEL_EXTRA_TREND_COLUMNS,
  TREND_CONDITION_STATS,
  TREND_PANELS,
  TREND_SIGNAL_STATS,
} from '@/trendData/trendData.constants';
import type { ReactSelectOption } from '@/core/IconSelect.molecule';
import type { PushOption } from '@/core/flux.service';
import type { TableColumnFilter } from '@/core/tableUtilities/tables.types';
import type { CustomLabelTargetUpdate, PropertyColumn, StatisticColumn } from '@/trendData/trendData.constants';

const DESCRIPTION = 'description';
const DATASOURCE_NAME = 'datasourceName';

/**
 * Sets the chart configuration mode
 */
export function setCustomizationMode(customizationMode: CUSTOMIZATION_MODES) {
  flux.dispatch('TREND_SET_CUSTOMIZATION_MODE', { customizationMode });
}

/**
 * Sets the visibility of the condition names displayed in the capsule lane.
 *
 * @param show - Whether or not to display capsule names
 */
export function setCapsuleLaneLabels(show: boolean) {
  flux.dispatch('TREND_SET_SHOW_CAPSULE_LABELS', { showCapsuleLabels: show });
}

/**
 * Toggles the visibility of the signal names and units of measure displayed in the signal lane.
 *
 * @param property - The property to be set, must be one of LABEL_PROPERTIES
 * @param value - The value to be set, must be one of LABEL_LOCATIONS
 */
export function setLabelDisplayConfiguration(property: string, value: string) {
  const payload = {
    property,
    value,
  };
  flux.dispatch('TREND_SET_LABEL_DISPLAY_CONFIG', payload);
}

/**
 * Sets the visibility of the gridlines on the chart.
 * @param showGridlines - true if we want to turn on gridlines, false if we want to turn off gridlines
 * @param skipWarning - (optional, defaults to false) true if we want to skip the "Gridlines can not be shown..."
 *  warning (i.e. if the caller has already shown the warning)
 */
export function setGridlines(showGridlines: boolean, skipWarning = false) {
  const payload = { showGridlines, skipWarning };
  flux.dispatch('TREND_SET_GRIDLINES', payload);
}

export function setIsCompareMode(isCompareMode: boolean) {
  flux.dispatch('TREND_SET_IS_COMPARE_MODE', { isCompareMode });
}

/**
 * Sets the sort order of a column in a panel.
 *
 * @param panel - The name of the panel. Must be one of TREND_PANELS
 * @param sortBy - The name of the column by which to sort
 * @param sortAsc - True to sort ascending, false otherwise
 * @param option - One of the WORKSTEP_PUSH constants
 *
 * @throws TypeError if the panel is not recognized.
 */
export function setPanelSort(panel: TREND_PANELS, sortBy: string, sortAsc: boolean, option?: PushOption) {
  if (!_.includes(_.values(TREND_PANELS), panel)) {
    throw new TypeError(`${panel} is not a valid panel`);
  }

  flux.dispatch('TREND_SET_PANEL_SORT', { panel, sortBy, sortAsc }, option);

  if (panel === TREND_PANELS.CAPSULES) {
    setCapsulePanelOffset(0);
  }
}

/**
 * Toggles the sort order of a column in a panel. If switching sort to a new column it defaults to sorting in
 * ascending order.
 *
 * @param panel - The name of the panel. Must be one of TREND_PANELS
 * @param sortBy - The name of the column by which to sort
 */
export function togglePanelSort(panel: TREND_PANELS, sortBy: string) {
  if (!_.includes(_.values(TREND_PANELS), panel)) {
    throw new TypeError(`${panel} is not a valid panel`);
  }

  const currentSort = sqTrendStore.getPanelSort(panel);
  const sortAsc = currentSort.sortBy === sortBy ? !currentSort.sortAsc : true;
  setPanelSort(panel, sortBy, sortAsc);
}

/**
 * Enables/Disables a column in a panel and then fetches the data to populate the column
 *
 * @param {String} panel - The name of the panel. Must be one of TREND_PANELS
 * @param {String|Object} column - The name of the column to enable/disable, or a column definition
 * @param {Boolean} enabled - True if the column is enabled, false otherwise
 * @param {String} [option] - One of the WORKSTEP_PUSH constants
 * @throws TypeError if the column or panel is not recognized.
 */
export function setColumnEnabled(
  panel: TREND_PANELS,
  column: string | { key: string },
  enabled: boolean,
  option?: PushOption,
) {
  flux.dispatch(
    'TREND_SET_COLUMN_ENABLED',
    {
      panel,
      column: _.isString(column) ? column : column.key,
      columnDefinition: _.isString(column) ? undefined : column,
      enabled,
    },
    option,
  );

  if (enabled && _.some(TREND_SIGNAL_STATS.concat(TREND_CONDITION_STATS), ['key', column])) {
    fetchDataBasedOnView();
    if (!sqTrendStore.isTrendViewCapsuleTime()) {
      fetchAllStatistics(true);
    }
  }

  if (
    enabled &&
    (column === DESCRIPTION ||
      column === DATASOURCE_NAME ||
      _.some(sqTrendStore.propertyColumns(TREND_PANELS.SERIES), _.flow(_.property('key'), _.partial(_.eq, column))))
  ) {
    fetchPropsForAllItems();
  }

  if (
    enabled &&
    sqTrendStore.customizationMode !== CUSTOMIZATION_MODES.OFF &&
    !_.includes(SERIES_PANEL_EXTRA_TREND_COLUMNS, column)
  ) {
    setCustomizationMode(CUSTOMIZATION_MODES.OFF);
  }

  if (enabled && panel === TREND_PANELS.CAPSULES) {
    fetchTableAndChartCapsules();
  }

  if (panel === TREND_PANELS.CHART_CAPSULES) {
    recomputeChartCapsules(PUSH_IGNORE);
  }
}

/**
 * Toggles a particular column on or off and then refreshes the statistics.
 * Hides the customize panel if it is showing and the column is being toggled on so the statistics are visible.
 *
 * @param panel - The name of the panel. Must be one of TREND_PANELS
 * @param columnKey - The column being toggled. Must be one of TREND_COLUMNS.
 */
export function toggleTrendColumn(panel: TREND_PANELS, columnKey: string): boolean {
  const shouldEnable = !sqTrendStore.isColumnEnabled(panel, columnKey);
  setColumnEnabled(panel, columnKey, shouldEnable);
  if (
    shouldEnable &&
    panel === TREND_PANELS.CHART_CAPSULES &&
    !sqTrendStore.isColumnEnabled(TREND_PANELS.CAPSULES, columnKey)
  ) {
    setColumnEnabled(TREND_PANELS.CAPSULES, columnKey, true);
  }
  if (panel !== TREND_PANELS.CHART_CAPSULES) {
    removeColumnFilter(columnKey, panel);
  }

  return shouldEnable;
}

/**
 * Toggles a custom statistics column for the capsule panel.
 *
 * @param statistic - One of the TREND_SIGNAL_STATS
 * @param itemId - id of a series
 */
export function toggleStatisticsColumn(statistic: StatisticColumn, itemId: string) {
  const column = {
    key: `${statistic.key}.${itemId}`,
    referenceSeries: itemId,
    statisticKey: statistic.key,
  };
  const panel = TREND_PANELS.CAPSULES;
  const wasEnabled = sqTrendStore.isColumnEnabled(panel, column.key);
  setColumnEnabled(panel, column, !wasEnabled);
}

/**
 * Adds a property column that retrieves propertyName from the item. Column will be enabled.
 *
 * @param panel - The name of the panel. Must be one of TREND_PANELS
 * @param propertyInput - object to be used as a column template definition.
 * @param forceEnabled - True to force the column enabled, regardless of its current status
 */
export function addPropertiesColumnToTrend(
  panel: TREND_PANELS,
  propertyInput: PropertyColumn | (Partial<PropertyColumn> & { propertyName: string }),
  forceEnabled = false,
) {
  const property = _.assign(
    {
      key: PROPERTIES_COLUMN_PREFIX + propertyInput.propertyName,
      uomKey: PROPERTIES_UOM_COLUMN_PREFIX + propertyInput.propertyName,
    },
    propertyInput,
  );
  flux.dispatch('TREND_ADD_PROPERTY_COLUMN', { panel, property }, PUSH_IGNORE);
  setColumnEnabled(panel, property.key, forceEnabled || !sqTrendStore.isColumnEnabled(panel, property.key));
  if (panel === TREND_PANELS.CHART_CAPSULES && !sqTrendStore.isColumnEnabled(TREND_PANELS.CAPSULES, property.key)) {
    flux.dispatch('TREND_ADD_PROPERTY_COLUMN', { panel: TREND_PANELS.CAPSULES, property }, PUSH_IGNORE);
    setColumnEnabled(
      TREND_PANELS.CAPSULES,
      property.key,
      forceEnabled || !sqTrendStore.isColumnEnabled(TREND_PANELS.CAPSULES, property.key),
    );
  }
}

export function addMultiplePropertiesColumn(
  panel: TREND_PANELS,
  propertyInputs: Array<Partial<PropertyColumn> & { propertyName: string }>,
  forceEnabled = false,
) {
  const properties = _.map(propertyInputs, (propertyInput) =>
    _.assign(
      {
        key: PROPERTIES_COLUMN_PREFIX + propertyInput.propertyName,
        uomKey: PROPERTIES_UOM_COLUMN_PREFIX + propertyInput.propertyName,
      },
      propertyInput,
    ),
  );
  flux.dispatch('TREND_ADD_MULTIPLE_PROPERTY_COLUMNS', { panel, properties }, PUSH_IGNORE);
  _.forEach(properties, (property) =>
    setColumnEnabled(panel, property.key, forceEnabled || !sqTrendStore.isColumnEnabled(panel, property.key)),
  );
}

/**
 * Removes a property from TREND_PANELS.CHART_CAPSULES
 *
 * @param property
 */
export function removeChartCapsulesProperty(property: PropertyColumn) {
  setColumnEnabled(TREND_PANELS.CHART_CAPSULES, property.key, false, PUSH_IGNORE);
  flux.dispatch('TREND_REMOVE_PROPERTY_COLUMN', {
    panel: TREND_PANELS.CHART_CAPSULES,
    property,
  });
}

/**
 * Removes a property column
 *
 * @param panel - The name of the panel. Must be one of TREND_PANELS
 * @param property - column definition (created by `addPropertiesColumn`).
 */
export function removePropertiesColumn(panel: TREND_PANELS, property: PropertyColumn) {
  if (sqTrendStore.isColumnEnabled(panel, property.key)) {
    setColumnEnabled(panel, property.key, false, PUSH_IGNORE);
  }

  flux.dispatch('TREND_REMOVE_PROPERTY_COLUMN', { panel, property });
  if (panel === TREND_PANELS.CAPSULES && sqTrendStore.isColumnEnabled(TREND_PANELS.CHART_CAPSULES, property.key)) {
    removeChartCapsulesProperty(property);
  }
  if (panel !== TREND_PANELS.CHART_CAPSULES) {
    const keyToRemove = typeof property.propertyName === 'string' ? property.propertyName : property.key;
    removeColumnFilter(keyToRemove, panel);
  }
}

/**
 * Updates the target for custom labels
 *
 * @param targetUpdates - an array of current and new target(s) for one or more custom labels
 */
export function updateCustomLabelTargets(targetUpdates: CustomLabelTargetUpdate[], pushOption?: PushOption) {
  flux.dispatch('TREND_UPDATE_CUSTOM_LABEL_TARGETS', { targetUpdates }, pushOption);
}

/**
 * Sets or removes a custom label for a lane or axis
 *
 * @param location - the location of the target (lane or axis) one of LABEL_LOCATIONS
 * @param target - the name of the axis or lane
 * @param text - the text to set the custom label or undefined or empty text to remove label
 */
export function setCustomLabel<LabelLocation extends 'lane' | 'axis'>(
  location: LabelLocation,
  target: LabelLocation extends 'lane' ? number : string,
  text: string,
) {
  if (_.isEmpty(_.trim(text))) {
    flux.dispatch('TREND_REMOVE_CUSTOM_LABEL', {
      location,
      target,
    });
  } else {
    flux.dispatch('TREND_SET_CUSTOM_LABEL', {
      location,
      target,
      text,
    });
  }
}

/**
 * Sets column filter
 *
 * @param key - column key of the column being filtered
 * @param filter - filter to be applied to the column
 * */
export function setColumnFilter(key: string, filter: TableColumnFilter | undefined) {
  const currentColumnFilter = sqTrendStore.getColumnFilter(key);
  if ((!filter && !currentColumnFilter) || _.isEqual(currentColumnFilter, filter)) {
    return;
  }

  flux.dispatch('TREND_SET_COLUMN_FILTER', { key, filter });
  resetCapsulePanelOffset();
  fetchTableAndChartCapsules();
  fetchChartCapsules();
  fetchPlot();
}

/**
 * Removes filter from columnFilters by setting it to undefined.
 *
 * @param key - column key for which column to remove the filter
 * @param panel - The panel the column is in
 * @throws {Error} if `panel` is equal to `TREND_PANELS.CHART_CAPSULES`
 * */
export function removeColumnFilter(key: string, panel: TREND_PANELS) {
  if (panel === TREND_PANELS.CHART_CAPSULES) {
    throw new Error(`Removing filters is not supported for ${TREND_PANELS.CHART_CAPSULES}`);
  }

  setColumnFilter(key, undefined);
}

/**
 * Sets the format string for a panel column.
 *
 * @param key - The column key of the column to format
 * @param format - The format string
 * @param panel - The panel the column is in
 * */
export function setColumnFormat(key: string, format: string, panel: TREND_PANELS) {
  flux.dispatch('TREND_SET_COLUMN_FORMAT', { key, format, panel });
  if (panel === TREND_PANELS.CAPSULES) {
    recomputeChartCapsules(PUSH_IGNORE);
  }
}

/**
 * Returns a comma separated list of the currently selected stats - ready for spikeCatcher formula input
 */
export function getEnabledColumns() {
  const stats = _.chain(TREND_SIGNAL_STATS)
    .filter((stat: any) => sqTrendStore.isColumnEnabled(TREND_PANELS.SERIES, stat.key))
    .map((stat: any) => stat.stat)
    .value();

  return _.get(stats, 'length') ? `, ${_.join(stats, ', ')}` : '';
}

/**
 * Sets the list of property options to separate and/or color by in compare mode
 *
 * @param {ReactSelectOption<string>[]} capsuleProperties - list of property options
 */
export function setCapsuleProperties(capsuleProperties: ReactSelectOption<string>[]) {
  flux.dispatch('TREND_SET_CAPSULE_PROPERTIES', { capsuleProperties });
}

/**
 * Sets the property to color by in compare mode
 *
 * @param {String} colorByProperty - property to use for coloring in compare mode
 */
export function setColorByProperty(colorByProperty: string | undefined) {
  flux.dispatch('TREND_SET_COLOR_BY_PROPERTY', { colorByProperty });
}

/**
 * Sets the property to separate by in compare mode
 *
 * @param {String} separateByProperty - property to use for separation in compare mode
 */
export function setSeparateByProperty(separateByProperty: string | undefined) {
  flux.dispatch('TREND_SET_SEPARATE_BY_PROPERTY', { separateByProperty });
}

/**
 * Sets the first column in compare mode
 *
 * @param {String} payload.firstColumn - first column
 */
export function setFirstColumn(firstColumn: string) {
  flux.dispatch('TREND_SET_FIRST_COLUMN', { firstColumn });
}

export function setShowSelectedRegionOptions(show: boolean) {
  flux.dispatch(
    'TREND_SET_SHOW_SELECTED_REGION_OPTIONS',
    {
      showSelectedRegionOptions: show,
    },
    PUSH_IGNORE,
  );
}
